import axios from 'axios';

import {getAccessToken} from './auth';

// FIXME 20.10.23 da anscheinend mit Datenmengen > 100 pro Tabelle hier gearbeitet wird und die Listings im
//  PCO Frontend nie dafür ausgelegt waren, um mit solchen Datenmengen und der Batch-Abfrage der ListViews zu
//  arbeiten und somit default nur 100 Datensätze je Tabelle abgerufen werden können, erhöhen wir vorläufig die
//  maximale Anzahl an Zeilen pro Batch auf 1000. Idealerweise müsste das Listing, so wie im Backed, in Batches die
//  Daten abrufen und entsprechend dann darstellen. Im Zuge dessen macht es dann ggf. auch Sinn ReactTable
//  abzuschaffen und Filterung und Co. Serverseitig zu machen.
const LISTING_MAX_PER_PAGE_OVERRIDE_QUERY_PARAM = '?maxPerPage=1000';

class Api {

    constructor() {
        this.axios = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            headers: {
                // Der JWT der wird nicht in dem Standard Authorization Header gesendet
                [process.env.REACT_APP_API_JWT_HEADER]: {
                    // Den Token immer aktuellen Token abrufen, da dieser im Hintergund laufend erneuert wird.
                    toString: () => {
                        const token = getAccessToken();
                        return token ? `Bearer ${token}` : '';
                    }
                }
            }
        });

        // request interceptor
        this.axios.interceptors.request.use(function (config) {
            return config;
        }, function (error) {
            // TODO im Moment wird der request on error einfach rejected, vllt ne dedizierte Errorpage?
            return Promise.reject(error);
        });
    }

    /**
     * @param slug
     * @return {Promise<any>}
     */
    fetchCallForPapers(slug) {
        return this.axios.get(`/pco/callforpapers/${slug}`)
            .then((res) => res.data);
    }

    /**
     * Holt das C4P einer bereits existierenden Einreichung samt Eingaben
     *
     * @param submissionId
     * @param token
     * @return {Promise<any>}
     */
    fetchPaperSubmission(submissionId) {
        return this.axios.get(`/pco/submission/edit/${submissionId}`)
            .then((res) => res.data)
            .catch((error) => {
                return Promise.reject();
            });
    }

    /**
     * Updatet eine existierende Einreichung anhand der aktuell gepublishten C4P Version
     *
     * @param submissionId
     * @param paperSubmission
     * @return {Promise<*>}
     */
    updatePaperSubmission(submissionId, paperSubmission) {
        return this.axios.post(`/pco/submission/edit/${submissionId}`, paperSubmission)
            .then((res) => res.data)
            .catch((error) => {
                return Promise.reject();
            });
    }

    /**
     * @return {Promise<any>}
     * @param callForPapersId
     */
    fetchRoles(callForPapersId) {
        return this.axios.get(`/pco/callforpapers/${callForPapersId}/roles`)
            .then((res) => res.data);
    }

    /**
     * @param slug
     * @param paperSubmission
     * @return {Promise<any>}
     */
    submitPaper(slug, paperSubmission) {
        return this.axios.post(`/pco/submission/${slug}/paper`, paperSubmission)
            .then((res) => res.data);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchSubmissionsForUser() {
        return this.axios.get(
            `/pco/submission/search${LISTING_MAX_PER_PAGE_OVERRIDE_QUERY_PARAM}`
        ).then((res) => res.data);
    }

    /**
     * Ruft alle verplanten Programmpunkte ab bei denen der Benutzer involviert ist
     *
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchSessionsForUser() {
        return this.axios.get(
            `/pco/session${LISTING_MAX_PER_PAGE_OVERRIDE_QUERY_PARAM}`
        ).then((res) => res.data);
    }

    /**
     *
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchTaskCountForUser() {
        return this.axios.get(`/pco/submission/taskcount`).then((res) => res.data);
    }

    /**
     * Ruft von der API ab, ob diese Einreichung bereits innerhalb von Programmpunkten referenziert und somit verplant
     * ist.
     *
     * @param paperSubmissionId
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchHasSubmissionBeenPlanned(paperSubmissionId) {
        return this.axios.get(`/pco/submission/hasbeenplanned/${paperSubmissionId}`).then((res) => res.data);
    }

    /**
     * Holt die Personendaten des eingeloggten Benutzers. Ggf. wird ein neuer Persondatensatz durch die API erzeugt.
     * @returns {Promise<AxiosResponse<Person>>}
     */
    fetchSelf() {
        return this.axios.get(`/pco/person/self`).then((res) => res.data);
    }

    /**
     * Aktualisiert die Personendaten des eingeloggten Benutzers
     *
     * @return {Promise<*>}
     * @param person
     */
    updateSelf(person) {
        return this.axios.post(`/pco/person/self`, person)
            .then((res) => res.data);
    }

    /**
     *
     * @param callForPapersId
     * @return {Promise<AxiosResponse<any>>}
     */
    fetchReviewConfigurationForCallForPapers(callForPapersId) {
        return this.axios.get(`/pco/eventreviewconfiguration/${callForPapersId}`).then((res) => res.data);
    }

    /**
     * @returns {Promise<AxiosResponse<any>>}
     */
    fetchAllSubmissionReviewsForUser() {
        return this.axios.get(
            `/pco/submission/getSubmissionReviewsForUser${LISTING_MAX_PER_PAGE_OVERRIDE_QUERY_PARAM}`
        ).then((res) => res.data);
    }

    /**
     *
     * @param reviewId
     */
    fetchSubmissionReview(reviewId) {
        return this.axios.get(`/pco/submission/review/${reviewId}`).then((res) => res.data);
    }

    /**
     * Updated eine neue Review für eine Submission
     *
     * @param submissionId
     * @param reviewId
     * @param paperSubmissionReview
     * @return {Promise<*>}
     */
    updateSubmissionReview(submissionId, reviewId, paperSubmissionReview) {
        return this.axios.post(`/pco/submission/${submissionId}/review/${reviewId}`, paperSubmissionReview)
            .then((res) => res.data)
            .catch(() => {
                return Promise.reject();
            });
    }

    /**
     * Ruft die Keycloak-Daten des Referenten einer Einreichung ab.
     *
     * @param submissionId
     */
    fetchSubmissionKeycloakSpeaker(submissionId) {
        return this.axios.get(`/pco/submission/${submissionId}/speaker`)
            .then((res) => res.data)
            .catch(() => {
                return Promise.reject();
            });
    }
}

export const api = new Api();
